import {
  ConditionExpression,
  ConditionOperator,
  ICondition,
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../models";
import { FIELD_NAME_PATTERN } from "../AppSettingsSchema/logicValidation";

export type EditableSimpleCondition = Omit<ICondition, "op"> & {
  op: ConditionOperator;
};

export type ConditionFieldShape = ICondition;

export type ConditionValueEditorKind =
  | "none"
  | "string"
  | "number"
  | "stringArray"
  | "numberArray";

function isSelectOptionObject(
  item: unknown,
): item is { label: string; value: string | number } {
  return (
    typeof item === "object"
    && item !== null
    && "label" in item
    && "value" in item
    && typeof (item as { label: unknown }).label === "string"
  );
}

function isConditionFieldShape(value: unknown): value is ConditionFieldShape {
  return (
    typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && "field" in value
    && "op" in value
    && typeof (value as { field: unknown }).field === "string"
    && typeof (value as { op: unknown }).op === "string"
  );
}

function isOrCondition(value: unknown): value is { OR: ConditionExpression[] } {
  return (
    typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && "OR" in value
    && Array.isArray((value as { OR: unknown }).OR)
  );
}

function isNotCondition(value: unknown): value is { NOT: ConditionExpression } {
  return (
    typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && "NOT" in value
  );
}

function isSupportedSimpleOperator(value: unknown): value is ConditionOperator {
  return Object.values(ConditionOperator).includes(value as ConditionOperator);
}

function fieldUsesNumericConditionValues(field: TVerificationAppFieldConfigItem | undefined): boolean {
  if (!field || field.type !== TConfigItemType.SELECT) {
    return false;
  }

  const data = (field.data ?? []) as unknown[];

  return data.length > 0 && data.every(
    (item) => isSelectOptionObject(item) && typeof item.value === "number",
  );
}

function getConditionSourceFieldName(
  field: TVerificationAppFieldConfigItem,
): string | null {
  if (field.type === TConfigItemType.NOTE || typeof field.name !== "string") {
    return null;
  }

  return FIELD_NAME_PATTERN.test(field.name) ? field.name : null;
}

function normalizeConditionValue(
  value: unknown,
  kind: Exclude<ConditionValueEditorKind, "none">,
): EditableSimpleCondition["value"] {
  switch (kind) {
    case "string":
      return typeof value === "string" ? value : "";
    case "number":
      return typeof value === "number" && !Number.isNaN(value) ? value : 0;
    case "stringArray":
      return Array.isArray(value) && value.every((item) => typeof item === "string")
        ? value
        : [];
    case "numberArray":
      return Array.isArray(value) && value.every((item) => typeof item === "number")
        ? value
        : [];
  }
}

export function conditionNeedsValue(op: ConditionOperator): boolean {
  return op !== ConditionOperator.EXISTS && op !== ConditionOperator.NOT_EXISTS;
}

export function conditionUsesArrayValue(op: ConditionOperator): boolean {
  return op === ConditionOperator.IN || op === ConditionOperator.NOT_IN;
}

export function getConditionFieldOptions(
  fields: TVerificationAppFieldConfigItem[],
): string[] {
  const seen = new Set<string>();
  const options: string[] = [];

  fields.forEach((field) => {
    const fieldName = getConditionSourceFieldName(field);

    if (!fieldName || seen.has(fieldName)) {
      return;
    }

    seen.add(fieldName);
    options.push(fieldName);
  });

  return options;
}

export function isEditableSimpleCondition(value: unknown): value is EditableSimpleCondition {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.field === "string"
    && isSupportedSimpleOperator(candidate.op)
    && (candidate.doc === undefined)
    && (candidate.const === undefined)
    && !("OR" in candidate)
    && !("NOT" in candidate)
  );
}

export function getSimpleConditionValueEditorKind(
  condition: EditableSimpleCondition,
  availableFields: TVerificationAppFieldConfigItem[],
): ConditionValueEditorKind {
  if (!conditionNeedsValue(condition.op)) {
    return "none";
  }

  const referencedField = availableFields.find(
    (field) => getConditionSourceFieldName(field) === condition.field,
  );
  const scalarKind: ConditionValueEditorKind = fieldUsesNumericConditionValues(referencedField)
    ? "number"
    : "string";

  if (conditionUsesArrayValue(condition.op)) {
    return scalarKind === "number" ? "numberArray" : "stringArray";
  }

  return scalarKind;
}

export function applyConditionValueEditorKind(
  condition: EditableSimpleCondition,
  kind: ConditionValueEditorKind,
): EditableSimpleCondition {
  if (kind === "none") {
    const { value: _removed, ...rest } = condition;
    return rest;
  }

  return {
    ...condition,
    value: normalizeConditionValue(condition.value, kind),
  };
}

export function createSimpleCondition(
  availableFields: TVerificationAppFieldConfigItem[],
): EditableSimpleCondition {
  const firstFieldName = getConditionFieldOptions(availableFields)[0] ?? "";
  const nextCondition: EditableSimpleCondition = {
    field: firstFieldName,
    op: ConditionOperator.EQUALS,
  };

  return applyConditionValueEditorKind(
    nextCondition,
    getSimpleConditionValueEditorKind(nextCondition, availableFields),
  );
}

export function getConditionFieldError(
  condition: EditableSimpleCondition,
  availableFields: TVerificationAppFieldConfigItem[],
): string | null {
  if (!condition.field.trim()) {
    return "Select a current or earlier field in this step.";
  }

  return getConditionFieldOptions(availableFields).includes(condition.field)
    ? null
    : "Condition field must reference the current or an earlier field in this step.";
}

function formatConditionReference(condition: ConditionFieldShape): string {
  return condition.doc ? `${condition.doc}.${condition.field}` : condition.field;
}

function formatConditionValue(value: unknown): string {
  if (typeof value === "string") {
    return `"${value}"`;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => formatConditionValue(item)).join(", ")}]`;
  }

  if (value === undefined) {
    return "?";
  }

  return JSON.stringify(value);
}

function formatConditionOperand(condition: ConditionFieldShape): string {
  return condition.const
    ? `constants "${condition.const}"`
    : formatConditionValue(condition.value);
}

function formatLeafCondition(condition: ConditionFieldShape): string {
  const reference = formatConditionReference(condition);
  const operand = formatConditionOperand(condition);

  switch (condition.op) {
    case "equals":
      return `${reference} = ${operand}`;
    case "notEquals":
      return `${reference} != ${operand}`;
    case "greaterThan":
      return `${reference} > ${operand}`;
    case "lessThan":
      return `${reference} < ${operand}`;
    case "in":
      return `${reference} in ${operand}`;
    case "notIn":
      return `${reference} not in ${operand}`;
    case "exists":
      return `${reference} exists`;
    case "notExists":
      return `${reference} not exists`;
    case "condition":
      return `condition "${condition.field}"`;
    default:
      return `${reference} ${condition.op}${condition.const || condition.value !== undefined ? ` ${operand}` : ""}`;
  }
}

function formatConditionInlineInternal(condition: unknown): string {
  if (Array.isArray(condition)) {
    if (condition.length === 0) return "()";
    const parts = condition.map((item) => formatConditionInlineInternal(item));
    return parts.length === 1 ? parts[0] : `(${parts.join(" AND ")})`;
  }

  if (isOrCondition(condition)) {
    if (condition.OR.length === 0) return "()";
    const parts = condition.OR.map((item) => formatConditionInlineInternal(item));
    return parts.length === 1 ? parts[0] : `(${parts.join(" OR ")})`;
  }

  if (isNotCondition(condition)) {
    return `NOT (${formatConditionInlineInternal(condition.NOT)})`;
  }

  if (isConditionFieldShape(condition)) {
    return formatLeafCondition(condition);
  }

  return JSON.stringify(condition);
}

function formatConditionMultilineInternal(condition: unknown, indentLevel = 0): string {
  const indent = "  ".repeat(indentLevel);

  if (Array.isArray(condition)) {
    if (condition.length === 0) return `${indent}()`;
    if (condition.length === 1) return formatConditionMultilineInternal(condition[0], indentLevel);

    const lines = [`${indent}(`];
    condition.forEach((item, index) => {
      lines.push(formatConditionMultilineInternal(item, indentLevel + 1));
      if (index < condition.length - 1) {
        lines.push(`${indent}  AND`);
      }
    });
    lines.push(`${indent})`);
    return lines.join("\n");
  }

  if (isOrCondition(condition)) {
    if (condition.OR.length === 0) return `${indent}()`;
    if (condition.OR.length === 1) return formatConditionMultilineInternal(condition.OR[0], indentLevel);

    const lines = [`${indent}(`];
    condition.OR.forEach((item, index) => {
      lines.push(formatConditionMultilineInternal(item, indentLevel + 1));
      if (index < condition.OR.length - 1) {
        lines.push(`${indent}  OR`);
      }
    });
    lines.push(`${indent})`);
    return lines.join("\n");
  }

  if (isNotCondition(condition)) {
    const nested = formatConditionMultilineInternal(condition.NOT, indentLevel + 1);
    return `${indent}NOT (\n${nested}\n${indent})`;
  }

  if (isConditionFieldShape(condition)) {
    return `${indent}${formatLeafCondition(condition)}`;
  }

  return `${indent}${JSON.stringify(condition)}`;
}

export function conditionHasCompoundStructure(condition: unknown): boolean {
  if (Array.isArray(condition)) {
    return condition.length > 1 || condition.some((item) => conditionHasCompoundStructure(item));
  }

  if (isOrCondition(condition)) {
    return true;
  }

  if (isNotCondition(condition)) {
    return true;
  }

  return false;
}

export function formatConditionInline(condition: unknown): string {
  return formatConditionInlineInternal(condition);
}

export function formatConditionMultiline(condition: unknown): string {
  return formatConditionMultilineInternal(condition);
}
